import { useCallback, useEffect, useState } from 'react';
import { Flex, message, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSpin } from '@/components/SpinContext.jsx';
import { useToken } from '@/common/helper';
import StatusTag from '@/components/StatusTag.jsx';
import TeamBadge from '@/components/TeamBadge.jsx';
import { getSensorsDDL } from '@/common/utils/dropDown';
import { getProviderImagePath } from '@/common/utils/imageUtils';
import { getPermissions } from '@/common/helper/getPermissions';
import {
  DeleteSettingsCompanySensor,
  GetCompanySensorDetail,
  GetSettingsCompanySensors,
  SensorData,
} from '@/apis/settings';
import type { TeamData } from '@/apis/types';
import theme from '@/assets/theme';
import type { CredentialsModel } from '../../../Modals/SensorConnectionModal';
import type {
  ModalState,
  RootState,
  Sensor,
  SensorsController,
} from '../types';
import { DeleteIcon, PencilIcon } from '@/assets/icons/icons';

const getCredentialInstructions = (
  value?: SensorData['credential_description'] | string
): SensorData['credential_description'] | undefined => {
  if (Array.isArray(value)) return value;

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : undefined;
    } catch {
      return value.trim() ? [{ title: value }] : undefined;
    }
  }

  return undefined;
};

const buildSelectedSensor = (detailData: Partial<SensorData>): SensorData =>
  ({
    ...detailData,
    status: detailData.status || 'Checking Credentials',
    credential_description: getCredentialInstructions(
      detailData.credential_description
    ),
  }) as SensorData;

export const useSensorsController = (): SensorsController => {
  const { token } = useToken();
  const { t } = useTranslation('settings');
  const { showSpin, hideSpin } = useSpin();
  const mainMenu = useSelector((state: RootState) => state.menu.items);
  const sensorsPermissions = getPermissions(mainMenu, 'Settings', 'Sensors');

  const [showProviderDetails, setShowProviderDetails] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sensorList, setSensorList] = useState<SensorData[]>([]);
  const [sensorFormList, setSensorFormList] = useState<Sensor[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [credentialDrawerView, setCredentialDrawerView] = useState<
    'details' | 'credential'
  >('details');
  const [sensorDDLLoading, setSensorDDLLoading] = useState(true);
  const [modalState, setModalState] = useState<ModalState>({
    delete: false,
    connection: false,
  });
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState<SensorData | null>(null);
  const [credentials, setCredentials] = useState<CredentialsModel | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [maxPageSize, setMaxPageSize] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const handleFetchCompanySensors = useCallback(
    (pageNum: number = 1, searchVal: string = '') => {
      GetSettingsCompanySensors(token ?? '', pageNum, searchVal)
        .then((res) => {
          const data = res?.data || null;
          if (data?.data?.is_success) {
            const responseData = data.data.data as
              SensorData[] | { data?: SensorData[] } | undefined;
            const sensors = Array.isArray(responseData)
              ? responseData
              : Array.isArray(responseData?.data)
                ? responseData.data
                : [];
            setSensorList(sensors);
            setTotalPages(data?.data?.page_count ?? 1);
            setMaxPageSize(data?.data?.total_count ?? 0);
          } else {
            message.error(data?.data?.message || 'Failed to fetch sensors.');
          }
        })
        .catch((err) => {
          console.error('Error fetching sensors:', err);
        });
    },
    [token]
  );

  useEffect(() => {
    handleFetchCompanySensors(1, '');
  }, [handleFetchCompanySensors]);

  useEffect(() => {
    setSensorDDLLoading(true);
    const _ = getSensorsDDL(token ?? '')
      .then((val) => {
        if (val && Array.isArray(val)) {
          setSensorFormList(val);
        }
      })
      .finally(() => {
        setSensorDDLLoading(false);
      });
  }, [token]);

  const handleCloseDrawer = useCallback((): void => {
    setDrawerOpen(false);
    setSelectedSensor(null);
    setShowProviderDetails(false);
    setCredentialDrawerView('details');
    setSearchQuery('');
    handleFetchCompanySensors(page, search);
  }, [handleFetchCompanySensors, page, search]);

  const handleDeleteClick = (sensor: SensorData): void => {
    setSelectedSensor(sensor);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = (): void => {
    if (!selectedSensor?.id) return;

    DeleteSettingsCompanySensor(selectedSensor.id, token ?? '')
      .then((res) => {
        const data = res?.data || null;
        if (!data) return;

        if (data.data?.is_success) {
          message.success(data?.data?.message);
          setDeleteModalVisible(false);
          setModalState((prev) => ({ ...prev, delete: false }));
          handleCloseDrawer();
        } else {
          message.error(data?.data?.message);
        }
      })
      .catch(() => {
        hideSpin();
      });
  };

  const fetchSensorDetail = useCallback(
    async (sensorId: string): Promise<Partial<SensorData> | null> => {
      const res = await GetCompanySensorDetail(token ?? '', sensorId);
      const data = res?.data || null;

      if (data?.data?.is_success) {
        return (data.data.data || {}) as Partial<SensorData>;
      }

      message.error(data?.data?.message || 'Failed to fetch sensor detail.');
      return null;
    },
    [token]
  );

  const handleSelectProvider = useCallback(
    async (sensor: Sensor | SensorData): Promise<void> => {
      const sensorId = sensor?.id;
      if (!sensorId) return;

      showSpin();
      try {
        const detailData = await fetchSensorDetail(sensorId);
        if (!detailData) return;

        setSelectedSensor(buildSelectedSensor(detailData));
        setShowProviderDetails(true);
        setCredentialDrawerView('details');
      } catch (err) {
        console.error('Error fetching sensor provider detail:', err);
      } finally {
        hideSpin();
      }
    },
    [fetchSensorDetail, hideSpin, showSpin]
  );

  const handleEdit = async (record: SensorData): Promise<void> => {
    const sensorId = record?.sensor_id;
    if (!sensorId) return;

    showSpin();
    try {
      const detailData = await fetchSensorDetail(sensorId);
      if (!detailData) return;

      setSelectedSensor(buildSelectedSensor(detailData));
      setShowProviderDetails(true);
      setCredentialDrawerView('details');
      setIsEditing(true);
      setDrawerOpen(true);
    } catch (err) {
      console.error('Error fetching sensor detail:', err);
    } finally {
      hideSpin();
    }
  };

  const handleAddProvider = (): void => {
    setSelectedSensor(null);
    setIsEditing(false);
    setShowProviderDetails(false);
    setCredentialDrawerView('details');
    setDrawerOpen(true);
  };

  const handleDrawerBack = (): void => {
    if (showProviderDetails && credentialDrawerView === 'credential') {
      setCredentialDrawerView('details');
      return;
    }

    if (!showProviderDetails) return;

    if (isEditing) {
      handleCloseDrawer();
      return;
    }

    setShowProviderDetails(false);
    setCredentialDrawerView('details');
    setCredentials(null);
  };

  const handleSearchChange = (value: string = ''): void => {
    setSearch(value);
    setPage(1);
    handleFetchCompanySensors(1, value);
  };

  const handlePageChange = (newPage: number): void => {
    const validPage = newPage > 0 ? newPage : 1;
    setPage(validPage);
    handleFetchCompanySensors(validPage, search);
  };

  const columns: ColumnsType<SensorData> = [
    {
      title: t('sensor'),
      dataIndex: 'sensor_name',
      key: 'sensor_name',
      render: (text: string) => {
        const sensorName = text || '--';
        return (
          <Typography.Text
            ellipsis={{ tooltip: sensorName }}
            className='sensor-name-cell'
          >
            {sensorName}
          </Typography.Text>
        );
      },
    },
    {
      title: t('description'),
      dataIndex: 'sensor_description',
      key: 'description',
      render: (text: string, record: SensorData) => {
        const description = text || record?.description || '--';
        return (
          <Typography.Text
            ellipsis={{ tooltip: description }}
            className='sensor-description-cell'
          >
            {description}
          </Typography.Text>
        );
      },
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <div className='sensor-status-cell'>
          <StatusTag status={status} />
        </div>
      ),
    },
    {
      title: `${theme.themeType === 2 ? t('client') : t('team')}`,
      dataIndex: 'teams',
      key: 'teams',
      render: (_: unknown, record: SensorData) => {
        if (record?.is_all_teams) {
          return <TeamBadge text='ALL' Truncate={false} />;
        }

        return record?.teams?.length && record.teams.length > 0 ? (
          <div className='sensor-team-cell'>
            <TeamBadge
              color={record?.teams[0]?.color}
              text={`${record?.teams[0]?.name}`}
              tooltipText={`${record?.teams[0]?.name}`}
            />
            {record?.teams.length > 1 && (
              <Tooltip
                title={record?.teams
                  ?.slice(1)
                  .map((team: TeamData) => team.name)
                  .join(', ')}
                placement='top'
              >
                <div className='sensor-team-more'>
                  <TeamBadge
                    color={undefined}
                    text={`+ ${record.teams.length - 1}`}
                  />
                </div>
              </Tooltip>
            )}
          </div>
        ) : (
          <p>--</p>
        );
      },
    },
    {
      title: '',
      key: 'action',
      render: (_: unknown, record: SensorData) => (
        <Flex align='center' gap={6}>
          {record?.permissions?.is_update && (
            <Flex
              justify='center'
              align='center'
              className='action-icon-wrapper'
              role='button'
              aria-label='Edit'
              onClick={() => handleEdit(record)}
            >
              <PencilIcon color='var(--text-primary)' />
            </Flex>
          )}
          {record?.permissions?.is_delete && (
            <Flex
              justify='center'
              align='center'
              className='action-icon-wrapper'
              role='button'
              aria-label='Delete'
              onClick={() => handleDeleteClick(record)}
            >
              <DeleteIcon />
            </Flex>
          )}
        </Flex>
      ),
    },
  ];

  const totalCount = maxPageSize;
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);

  return {
    columns,
    credentials,
    credentialDrawerView,
    deleteModalVisible,
    drawerOpen,
    getProviderImagePath,
    handleAddProvider,
    handleCloseDrawer,
    handleConfirmDelete,
    handleDrawerBack,
    handleSelectProvider,
    handlePageChange,
    handleSearchChange,
    isEditing,
    maxPageSize,
    modalState,
    page,
    rangeEnd,
    rangeStart,
    searchQuery,
    selectedSensor,
    sensorDDLLoading,
    sensorFormList,
    sensorList,
    sensorsPermissions,
    setCredentialDrawerView,
    setDeleteModalVisible,
    setModalState,
    setSearchQuery,
    setSelectedSensor,
    setShowProviderDetails,
    showProviderDetails,
    t,
    totalPages,
  };
};
